<script module lang="ts">
  import { geoPath, geoConicEqualArea } from 'd3-geo';
  import { feature } from 'topojson-client';
  import { browser } from '$app/environment';
  import topoData from '../../../data/topojson/land-50m.json';
  import countryData from '../../../data/topojson/countries-50m.json';

  export const width = 150;
  export const height = 80;
  export const dpr = browser ? window.devicePixelRatio || 1 : 1;

  export const projection = geoConicEqualArea()
    .parallels([22, 30])
    .rotate([-54, 0])
    .center([0, 26])
    .scale(width * (360 / 160))
    .translate([width / 2, height / 2]);

  // Pre-render the static background (land + borders + labels) once,
  // shared across all 16 instances via drawImage().
  export let backgroundCanvas: OffscreenCanvas | null = null;

  if (browser) {
    backgroundCanvas = new OffscreenCanvas(width * dpr, height * dpr);
    const ctx = backgroundCanvas.getContext('2d')!;
    ctx.scale(dpr, dpr);

    const path = geoPath().projection(projection).context(ctx);

    ctx.beginPath();
    path(feature(topoData, topoData.objects.land));
    ctx.fillStyle = '#e8e8e8';
    ctx.fill();

    ctx.beginPath();
    path(feature(countryData, countryData.objects.countries));
    ctx.strokeStyle = '#999';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    ctx.fillStyle = '#999';
    ctx.font = '500 8px sans-serif';
    ctx.textAlign = 'center';
    ctx.letterSpacing = '0.04em';

    const iranPos = projection([55.5, 29.5]);
    if (iranPos) ctx.fillText('IRAN', iranPos[0], iranPos[1]);

    const saudiPos = projection([47.5, 21]);
    if (saudiPos) ctx.fillText('SAUDI ARABIA', saudiPos[0], saudiPos[1]);
  }
</script>

<script lang="ts">
  let { locator = $bindable([0, 0]) }: { locator: [number, number] } = $props();

  let canvasEl: HTMLCanvasElement | undefined = $state();

  $effect(() => {
    if (!canvasEl) return;

    const ctx = canvasEl.getContext('2d')!;
    // Reset transform each frame so redraws are idempotent
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    if (backgroundCanvas) {
      ctx.drawImage(backgroundCanvas, 0, 0, width, height);
    }

    // Track locator as a reactive dependency and draw the dot
    const pos = projection([locator[1], locator[0]]);
    if (pos) {
      ctx.beginPath();
      ctx.arc(pos[0], pos[1], 2, 0, Math.PI * 2);
      ctx.fillStyle = '#d53f00';
      ctx.strokeStyle = '#d53f00';
      ctx.lineWidth = 2;
      ctx.fill();
      ctx.stroke();
    }
  });
</script>

<div class="mini-locator">
  <canvas
    bind:this={canvasEl}
    width={width * dpr}
    height={height * dpr}
    style="width: {width}px; height: {height}px;"
  ></canvas>
</div>

<style lang="scss">
  .mini-locator {
    min-width: 100px;
    width: 100%;
    height: 100%;
    background-color: #acb9c3;
    border-radius: 4px;
    border: 1px solid #bbb;
    overflow: hidden;
    position: relative;
    canvas {
      display: block;
      image-rendering: crisp-edges;
    }
  }
</style>
